'use client';

import { runtimeConfig } from '@/config/runtime-config';
import { Button } from '@/components/ui/button';
import { useMediaQuerySafe } from '@/hooks/useMediaQuerySafe';
import Image from 'next/image';
import { useCallback } from 'react';
import { TooltipedElem } from '../TooltipedElem';
import { useTranslations } from 'next-intl';
import { copyToClipboard } from '@/lib/notify';

type Props = {
  url: string;
  title: string;
  size?: number;
  copiedText?: string;
};

export const ShareButton = ({ url, title, size = 36, copiedText }: Props) => {
  const isMobile = useMediaQuerySafe('(max-width: 1023px)');
  const t = useTranslations('product');
  const toastT = useTranslations('toasts');

  const getFullUrl = useCallback(() => {
    if (typeof window === 'undefined') return url;

    return new URL(url, window.location.origin).toString();
  }, [url]);

  const copyText = useCallback(() => {
    copyToClipboard(getFullUrl(), { error: toastT('copy_error') });
  }, [getFullUrl, toastT]);

  const shareUrl = useCallback(async () => {
    if (typeof navigator === 'undefined') return;

    const fullUrl = getFullUrl();

    if (runtimeConfig.isTelegramMiniApp) {
      const openTelegramLink = window.Telegram?.WebApp?.openTelegramLink;

      if (openTelegramLink) {
        const telegramShareUrl = new URL('https://t.me/share/url');
        telegramShareUrl.searchParams.set('url', fullUrl);
        telegramShareUrl.searchParams.set('text', title);
        openTelegramLink(telegramShareUrl.toString());
        return;
      }
    }

    if (navigator.share) {
      try {
        await navigator.share({ url: fullUrl, title });
        return;
      } catch (error: any) {
        console.error('error', error);
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
      }
    }
    copyToClipboard(fullUrl, { success: toastT('link_copied'), error: toastT('copy_error') });
  }, [getFullUrl, title, toastT]);

  if (isMobile) {
    return (
      <>
        <Button variant='ghost' size='icon' onClick={shareUrl} aria-label={t('share')} className='hover:bg-transparent'>
          <Image src={'/assets/icons/share-icon.svg'} height={size} width={size} alt='' />
        </Button>
      </>
    );
  }

  return (
    <TooltipedElem onClick={copyText} tooltipText={copiedText ?? t('url_copied')}>
      <Image src={'/assets/icons/share-icon.svg'} height={size} width={size} alt='' />
    </TooltipedElem>
  );
};
